<template>
  <div class="pr-6 ml-8 text-midea-text" v-if="user.permissions.includes('sa') || user.permissions.includes('admin')">

    <div class="flex justify-between">
      <ul class="flex flex-wrap gap-3 items-center">
        <li v-for="(card, idx) in dashboard" :key="idx" 
            class="shadow-md rounded-lg p-2 text-white transition ease-in-out duration-150 w-60"
            :class="{ 'bg-midea-primary': !card.type,
                      'bg-red-900': card.type === 'red',
                      'bg-red-700': card.type === 'nearlyred',
                      'bg-green-500': card.type === 'green',
                      'bg-yellow-600': card.type === 'orange'  }">
            <div class="text-xs uppercase">{{card.name}}</div>
            <div class="text-6xl uppercase font-extralight pt-2 text-right">{{card.value}}</div>
        </li>
      </ul>

      <div class="flex flex-col 3xl:flex-row max-w-fit gap-4">
        <Combobox
          :noCreate="true"
          v-model="searchFilter"
          :options="filterOptions"
        />
        <CustomButton type="white" class="gap-2 h-10" @click="downloadCustomerReport">
            <div v-if="!downloadingReport">
                <DownloadIcon class="w-5 h-5 inline"/> Download Customer Report
            </div>
            <LoadingSpinner :size="20" v-if="downloadingReport"/>
        </CustomButton>
      </div>
    </div>

    <div class="grid grid-cols-10 gap-4 mt-4">
      <div class="col-span-8">
        <LineChart
          :title="lineChartTitle"
          :tabs="chartTabs"
          v-model:currentTab="currentLineChartTab"
          :data="lineChartData"
          :loading="loadingLineChart"
          :formatDate="formatDate"
          @chartClick="onLineChartClick"
        />
      </div>

      <div class="col-span-2">
        <PieChart
            :title="pieChartTitle"
            :subtitle="pieChartFilter ? formatDate(pieChartFilter) : ''"
            :data="pieChartData"
            :legend="pieChartLabels"
            @clearFilter="pieChartFilter = ''"
        />
      </div>

      <div class="col-span-10">
        <DataTable
          ref="dataTableElem"
          class="flex-grow"
          :fields="dataFields"
          :keepHeaderWithSlots="true"
          :onUpdateData="(page) => onUpdateData(page, tableData)"
          :onSelectItem="() => {}"
          :onClickRow="selectTableItem"
          :allowFilter="true"
        >
                <template #row="slotProps">
                    <td class="pl-4 py-4" v-for="col in Object.keys(dataFields)" :key="col">
                        <div
                          class="break-all text-lg"
                          :class="parseInt(slotProps.value.item.id) === parseInt(searchFilter?.id) ? 'text-blue-500' : 'text-midea-text'"
                        >
                          {{slotProps.value.item[col]}}
                        </div>
                    </td>
                </template>
                <template #filter>
                    <TableFilters
                        :availableFilters="tableFilters"
                        v-model="selectedTableFilters"
                        @change="() => getTableData()"
                    />
                </template>
        </DataTable>
      </div>
      <div class="col-span-10">
        <div class="grid grid-cols-2 gap-6">
            <div>
                <div class="text-lg mb-2 pl-1 font-semibold">
                    Most Warranty Registrations
                </div>
                <DataTable
                    ref="warrantyLeagueTable"
                    class="flex-grow"
                    :fields="mostWarrantyFields"
                    :keepHeaderWithSlots="true"
                    :onUpdateData="(page) => onUpdateLeague(page, warrantyLeagueData)"
                    :onSelectItem="() => {}"
                    :onClickRow="() => {}"
                    :allowFilter="true"
                    :hidePagination="true"
                >
                        <template #row="slotProps">
                            <td class="pl-4 py-4" v-for="col in Object.keys(mostWarrantyFields)" :key="col" :class="{ 'rounded-b-lg': slotProps.value.itemIdx === warrantyLeagueData.length - 1 }">
                                <div
                                class="text-lg text-midea-text"
                                >
                                {{slotProps.value.item[col]}}
                                </div>
                            </td>
                        </template>
                        <template #filter>
                            <TableFilters
                                :availableFilters="availableWarrantyTableFilters"
                                v-model="warrantyTableFilters"
                                @change="() => getWarrantyLeagueTableData()"
                            />
                        </template>
                </DataTable>
            </div>
            <div>
                <div class="text-lg mb-2 pl-1 font-semibold">
                    Most Unregistered Devices
                </div>
                <DataTable
                    ref="unregisteredLeagueTable"
                    class="flex-grow"
                    :fields="mostUnregisteredFields"
                    :keepHeaderWithSlots="true"
                    :onUpdateData="(page) => onUpdateLeague(page, unregisteredLeagueData)"
                    :onSelectItem="() => {}"
                    :onClickRow="() => {}"
                    :allowFilter="true"
                    :hidePagination="true"
                >
                        <template #row="slotProps">
                            <td class="pl-4 py-4" v-for="col in Object.keys(mostUnregisteredFields)" :key="col" :class="{ 'rounded-b-lg': slotProps.value.itemIdx === unregisteredLeagueData.length - 1 }">
                                <div
                                class="text-lg text-midea-text"
                                >
                                {{slotProps.value.item[col]}}
                                </div>
                            </td>
                        </template>
                        <template #filter>
                            <TableFilters
                                :availableFilters="availableUnregisteredTableFilters"
                                v-model="unregisteredTableFilters"
                                @change="() => getUnregisteredLeagueTableData()"
                            />
                        </template>
                </DataTable>
            </div>
        </div>
      </div>

    </div>
      
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, watch } from 'vue'
  import LineChart from '../components/LineChart.vue'
  import axios from 'axios'
  import moment from 'moment'
  import Combobox from '../components/Combobox.vue'
  import DataTable from '../components/DataTable.vue'
  import PieChart from '../components/PieChart.vue'
  import TableFilters from '../components/TableFilters.vue'
  import CustomButton from '@/components/CustomButton.vue'
  import FileSaver from 'file-saver'
  import { DownloadIcon } from '@heroicons/vue/solid';
  import { useStore } from 'vuex'
  import { getTableSearch } from '../util/queryParams.js'
  import LoadingSpinner from '../components/LoadingSpinner.vue'

  const store = useStore()

  const DEFAULT_FILTERS = {
    "year": null,
    "month": null,
    "type": null
  }

  const tableLimit = ref(5)

  const dashboard = ref([])

  const pieChartFilter = ref('')

  const searchFilter = ref()
  const filterOptions = ref([])

  watch(searchFilter, getHistoricData)

  const tableFilters = ref(createTableFilters())

  const tableData = ref([])
  const dataTableElem = ref()
  const dataFields = {
    'name': 'Name',
    'type': 'Type',
    'sold': 'Sold',
    'registrations': 'Registrations',
    'outstandingRegistrations': 'Outstanding Registrations'
  }
  const selectedTableFilters = ref({...DEFAULT_FILTERS})


  const warrantyLeagueData = ref([])
  const warrantyLeagueTable = ref()
  const mostWarrantyFields = {
    'name': 'Name',
    'registrations': 'Registrations',
  }
  const warrantyTableFilters = ref({...DEFAULT_FILTERS})
  const availableWarrantyTableFilters = computed(() => getAvailableTableFilters(warrantyTableFilters))

  const unregisteredLeagueData = ref([])
  const unregisteredLeagueTable = ref()
  const mostUnregisteredFields = {
    'name': 'Name',
    'unregistered': 'Unregistered',
  }
  const unregisteredTableFilters = ref({...DEFAULT_FILTERS})
  const availableUnregisteredTableFilters = computed(() => getAvailableTableFilters(unregisteredTableFilters))

  const downloadingReport = ref(false)

  const chartTabs = [
    { id: 'year', text: 'Annual' },
    { id: 'month', text: 'Monthly' },
    { id: 'week', text: 'Weekly' }
  ]

  const aggMap = {
    'year': { aggregate: aggregateByYear, format: formatYear },
    'month': { aggregate: aggregateByMonth, format: formatMonth },
    'week': { aggregate: aggregateByWeek, format: formatWeek } ,
  }

  let loadingLineChartTimer = -1
  const loadingLineChart = ref(false)
  const currentLineChartTab = ref(chartTabs[0])
  const historicData = ref([])

  const formatDate = computed(() => aggMap[currentLineChartTab.value.id].format)

  const lineChartData = computed(() => {
    const aggregated = aggMap[currentLineChartTab.value.id].aggregate()

    const labels = aggregated.map(entry => entry.rawDate)
    const registrations = aggregated.map(entry => entry.registrations)
    const sold = aggregated.map(entry => entry.sold)
    const warrantyRegistrations = aggregated.map(entry => entry.warrantyRegistrations)

    const datasets= [
      {
        label: 'Sold',
        borderColor: '#009DDA',
        backgroundColor: '#009DDA',
        data: sold,
        lineTension: 0.3
      },
      {
        label: 'Registered',
        backgroundColor: '#3b82f6',
        borderColor: '#3b82f6',
        data: registrations,
        lineTension: 0.3
      },
      {
        label: 'Warranty Registrations',
        backgroundColor: '#1c3f78',
        borderColor: '#1c3f78',
        data: warrantyRegistrations,
        lineTension: 0.3
      },
      ]

    return {
      labels,
      datasets
    }
  })

  const pieChartLabels = [
        {
            title: 'Unregistered',
            color: '#009DDA'
        },
        {
            title: 'Registered',
            color: '#3b82f6'
        }
    ]

  const pieChartData = computed(() => {
    const filteredData = pieChartFilter.value
                         ? historicData.value.filter(item => formatDate.value(item.date) === formatDate.value(pieChartFilter.value))
                         : historicData.value

    const aggregated = filteredData.reduce((accumulator, current) => {
        accumulator.sold = current.sold + accumulator.sold
        accumulator.registrations = current.registrations + accumulator.registrations
        return accumulator
    }, { sold: 0, registrations: 0 })

     
    return {
        labels: pieChartLabels.map(item => item.title),
        datasets: [
            {
                backgroundColor: pieChartLabels.map(item => item.color),
                data: [
                    aggregated.sold > 0 ? Math.ceil(((aggregated.sold - aggregated.registrations) / aggregated.sold) * 100) : 0,
                    aggregated.sold > 0 ? Math.floor((aggregated.registrations / aggregated.sold) * 100) : 0,
                ]
            }
        ]
    }
  })

  const lineChartTitle = computed(() => !searchFilter?.value?.name ? 'Outdoor units registered and sold' : `Outdoor units registered and sold for ${searchFilter.value.name}`)
  const pieChartTitle = computed(() => !searchFilter?.value?.name ? 'Outdoor units registered and sold %' : `Outdoor units registered and sold % for ${searchFilter.value.name}`)

  const user = computed(() => store.getters.user)

  onMounted(() => {
    getTiles()
    getTableData()
    getHistoricData()
    getWarrantyLeagueTableData()
    getUnregisteredLeagueTableData()
  })

  function getAvailableTableFilters(filterModel) {
    const defaultFilters = createTableFilters()
    const tableFilterVal = 'value' in filterModel.value ? filterModel.value.value : filterModel.value

    if (tableFilterVal.type) {
        const typeMap = {
            "Midea": { key: "BDM", filter: "BizDev" },
            "Distributor": { key: "Distributor", filter: "Dist" }
        }

        const filterType = typeMap[tableFilterVal.type]
        defaultFilters[filterType.key] = tableData.value.filter(item => item.type === filterType.filter)
    }
    
    return defaultFilters
  }

  function createTableFilters() {
    const currentYear = Number(moment().format('yyyy'))
    return {
        "year": Array.from({ length: currentYear - 2021 + 1}, (_, index) => 2021 + index).map(item => ({ 'id': item, 'name': item})),
        "month": ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map(item => ({ 'id': item, 'name': item})),
        "type": ["Midea", "Distributor"].map(item => ({ 'id': item, 'name': item}))
    }
  }

  function onLineChartClick(date) {
    pieChartFilter.value = date
  }

  async function getHistoricData() {
    loadingLineChartTimer = setTimeout(() => loadingLineChart.value = true, 1000)

    try {
      const getQueryUrl = () => {
        const DEFAULT_URL = '/dashboard/units'
        if (!searchFilter?.value) { return DEFAULT_URL }

        const typeMap = { 'dist': 'company', 'bizdev': 'user' }
        const entry = tableData.value.find(row => row.id === searchFilter.value.id)
        if (!entry || !typeMap[entry.type.toLowerCase()]) { return DEFAULT_URL}

        return `${DEFAULT_URL}?${typeMap[entry.type.toLowerCase()]}=${entry.id}`
      }

      const query = await axios.get(getQueryUrl())
      historicData.value = query.data
    }
    catch (e) {
      console.log(e)
    }
    finally {
      clearTimeout(loadingLineChartTimer)
      loadingLineChart.value = false
    }

  }

  async function getTiles() {
    const query = await axios.get('/dashboard')
    dashboard.value = query.data
  }

  async function getTableData() {
    const tableFilterVal = 'value' in selectedTableFilters.value ? selectedTableFilters.value.value : selectedTableFilters.value

    if (tableFilterVal.type === 'Midea') {
        tableLimit.value = 40
    }
    else {
        tableLimit.value = 5
    }

    const searchVal = 'value' in selectedTableFilters.value ? selectedTableFilters.value.value : selectedTableFilters.value
    const searchQuery = getTableSearch(searchVal)
    const query = await axios.get(`/dashboard/breakdown?${searchQuery}`)
    tableData.value = query.data
    filterOptions.value = query.data.map(row => ({ id: row.id, name: row.name }))
    dataTableElem.value.requestRefresh()
  }

  async function getWarrantyLeagueTableData() {
    await getLeagueTableData('mostWarranty', warrantyTableFilters, warrantyLeagueData, warrantyLeagueTable)
  }

  async function getUnregisteredLeagueTableData() {
    await getLeagueTableData('mostUnregistered', unregisteredTableFilters, unregisteredLeagueData, unregisteredLeagueTable)
  }

  async function getLeagueTableData(path, filters, data, element) {
    const tableFilterVal = 'value' in filters.value ? filters.value.value : filters.value

    switch (tableFilterVal.type) {
        case "Midea":
            filters.value["Distributor"] = null
            break
        case "Distributor":
            filters.value["BDM"] = null
            break
        default:
            filters.value["BDM"] = null
            filters.value["Distributor"] = null
    }

    const searchVal = 'value' in filters.value ? filters.value.value : filters.value
    const searchQuery = getTableSearch(searchVal)
    const query = await axios.get(`/dashboard/${path}?${searchQuery}`)
    data.value = query.data
    element.value.requestRefresh()
  }

  async function onUpdateData(page, data) {
    const startIdx = page * tableLimit.value
    const numPages = Math.ceil(data.length / tableLimit.value)
    const items = data.slice(startIdx, startIdx + tableLimit.value)

    return { 
        page: page,
        pages: numPages,
        limit: tableLimit.value,
        count: data.length,
        items
    }
  }

  async function onUpdateLeague(page, data) {
    const startIdx = page * tableLimit.value
    const numPages = Math.ceil(data.length / 10)
    const items = data.slice(startIdx, startIdx + 10)

    return { 
        page: page,
        pages: numPages,
        limit: 10,
        count: data.length,
        items
    }
  }

  function aggregateByWeek() {
    const weeklyData = historicData.value.reduce((accumulator, current) => {
      const currentWeek = moment(current.week).format('DD-MM-YYYY')
      const existingEntry = accumulator.find(entry => entry.date === currentWeek)

      if (existingEntry) {
        existingEntry.sold = existingEntry.sold + current.sold
        existingEntry.registrations = existingEntry.registrations + current.registrations
        existingEntry.warrantyRegistrations = existingEntry.warrantyRegistrations + current.warrantyRegistrations
      }
      else {
        accumulator.push({
          date: currentWeek,
          rawDate: current.date,
          sold: current.sold,
          registrations: current.registrations,
          warrantyRegistrations: current.warrantyRegistrations
        })
      }

      return accumulator
    }, [])

    return weeklyData
  }

  function aggregateByMonth() {
    const monthlyData = historicData.value.reduce((accumulator, current) => {
      const currentMonth = moment(current.date).format('MMMM YYYY')
      const existingEntry = accumulator.find(entry => entry.date === currentMonth)

      if (existingEntry) {
        existingEntry.sold = existingEntry.sold + current.sold
        existingEntry.registrations = existingEntry.registrations + current.registrations
        existingEntry.warrantyRegistrations = existingEntry.warrantyRegistrations + current.warrantyRegistrations
      }
      else {
        accumulator.push({
          date: currentMonth,
          rawDate: current.date,
          sold: current.sold,
          registrations: current.registrations,
          warrantyRegistrations: current.warrantyRegistrations
        })
      }

      return accumulator
    }, [])

    return monthlyData
  }

  function aggregateByYear() {
    const annualData = historicData.value.reduce((accumulator, current) => {
      const currentYear = moment(current.date).format('YYYY')
      const existingEntry = accumulator.find(entry => entry.date === currentYear)

      if (existingEntry) {
        existingEntry.sold = existingEntry.sold + current.sold
        existingEntry.registrations = existingEntry.registrations + current.registrations
        existingEntry.warrantyRegistrations = existingEntry.warrantyRegistrations + current.warrantyRegistrations
      }
      else {
        accumulator.push({
          date: currentYear,
          rawDate: current.date,
          sold: current.sold,
          registrations: current.registrations,
          warrantyRegistrations: current.warrantyRegistrations
        })
      }

      return accumulator
    }, [])

    return annualData
  }

  function selectTableItem(itemId) {
    const selectedEntry = tableData.value.find(row => row.id === itemId)
    if (selectedEntry) {
      if (searchFilter?.value?.id === selectedEntry.id) { searchFilter.value = null }
      else { searchFilter.value = { id: selectedEntry.id, name: selectedEntry.name } }
    }
  }

  function formatWeek(label) {
    return `w/c ${moment(label).format('DD-MM-YYYY')}`
  }

  function formatMonth(label) {
    return `${moment(label).format('MMMM YYYY')}`
  }

  function formatYear(label) {
    return `${moment(label).format('YYYY')}`
  }

  async function downloadCustomerReport() {
    try {
        downloadingReport.value = true
        const getQueryUrl = () => {
            const DEFAULT_URL = '/dashboard/customerReport'
            if (!searchFilter?.value) { return DEFAULT_URL }

            const typeMap = { 'dist': 'Distributor', 'bizdev': 'BDM' }
            const entry = tableData.value.find(row => row.id === searchFilter.value.id)
            if (!entry || !typeMap[entry.type.toLowerCase()]) { return DEFAULT_URL}

            return `${DEFAULT_URL}?${typeMap[entry.type.toLowerCase()]}=${entry.id}`
        }

        const ret = await axios.get(getQueryUrl(), { responseType: 'arraybuffer' })

        const name = searchFilter?.value?.name ? `${searchFilter?.value?.name} - Customer Report.xlsx` : 'Customer Report.xlsx'  
        FileSaver.saveAs(new Blob([ret.data], {type:ret.headers['content-type']}), name)
    }
    catch (e) {
        console.log(e)
    }
    finally {
        downloadingReport.value = false
    }

  }
</script>